// src/components/PrincipalSalaryView.js
// Principal Salary View - Read-only salary management view with WhatsApp sharing

import React, { useState, useEffect } from "react";
import { Banknote, History } from "lucide-react";
import { useSchoolName, getSchoolEmailDomain } from "./schoolNameStore";
export default function PrincipalSalaryView({
  onBack
}) {
  const schoolName = useSchoolName();
  // ========== STATE VARIABLES ==========
  const [employees, setEmployees] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  });
  const [activeTab, setActiveTab] = useState("payments");
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ========== MODALS STATE ==========
  const [payslipData, setPayslipData] = useState(null);
  const [showWhatsAppToast, setShowWhatsAppToast] = useState(false);
  const [whatsAppToastMsg, setWhatsAppToastMsg] = useState('');

  // ========== FETCH DATA ==========
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const empRes = await fetch(`https://schoolcrm-fzcv.onrender.com/api/salaries/employees?month=${selectedMonth}`);
      if (!empRes.ok) throw new Error("Failed to fetch employees salary status");
      const empData = await empRes.json();
      setEmployees(empData);
      const txRes = await fetch(`https://schoolcrm-fzcv.onrender.com/api/salaries/transactions?month=${selectedMonth}`);
      if (txRes.ok) {
        const txData = await txRes.json();
        setTransactions(txData);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "An error occurred while loading data");
    } finally {
      setLoading(false);
    }
  };
  const fetchAllTransactions = async () => {
    try {
      const txRes = await fetch(`https://schoolcrm-fzcv.onrender.com/api/salaries/transactions`);
      if (txRes.ok) {
        const txData = await txRes.json();
        setTransactions(txData);
      }
    } catch (err) {
      console.error("Error fetching all transactions:", err);
    }
  };
  useEffect(() => {
    fetchData();
  }, [selectedMonth]);
  useEffect(() => {
    if (activeTab === "history") {
      fetchAllTransactions();
    } else if (activeTab === "payments" || activeTab === "config") {
      fetchData();
    }
  }, [activeTab]);

  // ========== WHATSAPP SHARE FUNCTION ==========
  const handleWhatsAppShare = emp => {
    const month = formatMonth(selectedMonth);
    const baseSalary = Number(emp.baseSalary || 0).toLocaleString('en-IN');
    const paidAmount = Number(emp.paymentDetails?.paidAmount || emp.baseSalary || 0).toLocaleString('en-IN');
    const receiptDate = emp.paymentDetails?.paymentDate ? new Date(emp.paymentDetails.paymentDate).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }) : new Date().toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const paymentMethod = emp.paymentDetails?.paymentMethod || 'Bank Transfer';
    const transactionId = emp.paymentDetails?.transactionId || 'N/A';
    const message = `*${schoolName.toUpperCase()} SMART CAMPUS*
Affiliated Secondary & Higher Secondary School
Salem, Tamil Nadu, India | Contact: support@${getSchoolEmailDomain(schoolName)}

*SALARY ADVICE SLIP - ${month.toUpperCase()}*

*Employee Name:* ${emp.name}
*Designation/Role:* ${emp.role}
*Salary Month:* ${month}
*Receipt Date:* ${receiptDate}
 *Employee Category:* ${emp.type}
*Payment Mode:* ${paymentMethod}

━━━━━━━━━━━━━━━━━━━━━━━━━
*PAY DETAILS*

*Basic monthly base salary rate*
 ₹ ${baseSalary}
 Contractual basic wages structure

*Disbursed Salary Amount*
 ₹ ${paidAmount}
   Actual net pay distributed

━━━━━━━━━━━━━━━━━━━━━━━━━
*TOTAL DISBURSED AMOUNT*
   ₹ ${paidAmount}

🆔 *Transaction ID:* ${transactionId}
*Status:* PAID & SETTLED
━━━━━━━━━━━━━━━━━━━━━━━━━

_This is a system-generated payslip._
_For queries: support@${getSchoolEmailDomain(schoolName)}_`;
    let phone = emp.phone || '';
    phone = phone.replace(/[^+\d]/g, '');
    if (!phone) {
      setWhatsAppToastMsg(" No phone number for this staff");
      setShowWhatsAppToast(true);
      setTimeout(() => setShowWhatsAppToast(false), 3000);
      return;
    }
    const encodedMsg = encodeURIComponent(message);
    const waUrl = `https://wa.me/${phone}?text=${encodedMsg}`;
    const newWindow = window.open(waUrl, '_blank');
    if (!newWindow || newWindow.closed) {
      window.location.href = waUrl;
    }
    setWhatsAppToastMsg(`Sharing payslip with ${emp.name}`);
    setShowWhatsAppToast(true);
    setTimeout(() => setShowWhatsAppToast(false), 3000);
  };

  // ========== HELPER FUNCTIONS ==========
  const formatMonth = mStr => {
    if (!mStr) return "";
    const [year, month] = mStr.split("-");
    const date = new Date(year, parseInt(month) - 1, 1);
    return date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric"
    });
  };

  // ========== CALCULATIONS ==========
  const totalPayroll = employees.reduce((sum, emp) => sum + (emp.baseSalary || 0), 0);
  const totalPaid = employees.reduce((sum, emp) => sum + (emp.status === "Paid" ? emp.paymentDetails?.paidAmount || 0 : 0), 0);
  const remainingPay = totalPayroll - totalPaid;
  const paidCount = employees.filter(emp => emp.status === "Paid").length;
  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) || emp.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "All" || emp.type === typeFilter;
    const matchesStatus = statusFilter === "All" || emp.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  // ========== RENDER ==========
  return <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&family=Inter:wght@300;400;500;600;700;800&display=swap');
        
        * { box-sizing: border-box; }
        body { font-family: 'Inter', sans-serif; background-color: #f8fafc; }
        
        .sal-card { transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); border: 1px solid #e2e8f0; }
        .sal-card:hover { transform: translateY(-3px); box-shadow: 0 12px 24px rgba(0, 0, 0, 0.05); }
        .t-row:hover { background-color: #f8fafc !important; }
        .badge-staff { background: #ede9fe; color: #5b21b6; border: 1px solid #ddd6fe; }
        .badge-worker { background: #ccfbf1; color: #0f766e; border: 1px solid #99f6e4; }
        .badge-paid { background: #d1fae5; color: #065f46; border: 1px solid #a7f3d0; }
        .badge-unpaid { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
        
        @media print {
          body * { visibility: hidden; }
          #payslip-print-area, #payslip-print-area * { visibility: visible; }
          #payslip-print-area { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; border: none !important; }
        }
        
        .tab-btn {
          border: none;
          background: transparent;
          color: #64748b;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .tab-btn.active {
          background: #fff;
          color: #312e81;
          box-shadow: 0 2px 6px rgba(0,0,0,0.06);
        }
        .tab-btn:hover {
          background: #e2e8f0;
        }
        
        .whatsapp-share-btn {
          background: #25D366;
          color: white;
          border: none;
          padding: 6px 14px;
          border-radius: 30px;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: 0.15s;
          box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
        }
        .whatsapp-share-btn:hover {
          background: #1da85c;
          transform: scale(0.96);
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .view-only-badge {
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 20px;
          padding: 4px 14px;
          font-size: 10px;
          font-weight: 700;
          color: #c7d2fe;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }
      `}</style>

      {/* ===== WHATSAPP TOAST ===== */}
      {showWhatsAppToast && <div style={{
      position: 'fixed',
      bottom: '30px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#0b1e2e',
      color: 'white',
      padding: '12px 24px',
      borderRadius: '50px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      animation: 'fadeIn 0.3s ease',
      fontFamily: "'Inter', sans-serif"
    }}>
          <i className="fab fa-whatsapp" style={{
        color: '#25D366',
        fontSize: '20px'
      }}></i>
          {whatsAppToastMsg}
        </div>}

      <div style={{
      minHeight: "100vh",
      backgroundColor: "#f8fafc",
      paddingBottom: 60
    }}>
        <div style={{
        width: "100%",
        padding: "0 24px",
        marginTop: 24
      }}>
          
          {/* ===== MONTH SELECTOR & TABS ===== */}
          <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          backgroundColor: "#fff",
          padding: "16px 20px",
          borderRadius: 16,
          border: "1px solid #e2e8f0",
          marginBottom: 24,
          boxShadow: "0 2px 8px rgba(0,0,0,0.02)"
        }}>
            <div style={{
            display: "flex",
            alignItems: "center",
            gap: 12
          }}>
              <label style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#475569",
              textTransform: "uppercase",
              letterSpacing: "0.5px"
            }}>Payroll Month:</label>
              <input type="month" value={selectedMonth} onChange={e => {
              if (e.target.value) setSelectedMonth(e.target.value);
            }} style={{
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              padding: "8px 12px",
              fontSize: 13,
              fontWeight: 600,
              color: "#1e293b",
              outline: "none",
              cursor: "pointer"
            }} />
            </div>
            
            <div style={{
            display: "flex",
            background: "#f1f5f9",
            borderRadius: 12,
            padding: 4,
            flexWrap: "wrap",
            gap: 4
          }}>
              {[{
              id: "payments",
              label: "Salary Payments",
              Icon: Banknote
            }, {
              id: "history",
              label: "Transaction History",
              Icon: History
            }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}>
                  <tab.Icon size={14} strokeWidth={2.3} /> {tab.label}
                </button>)}
            </div>
          </div>

          {/* ===== STATS SECTION ===== */}
          {activeTab !== "history" && <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 24
        }}>
              <div className="sal-card" style={{
            background: "#fff",
            padding: 20,
            borderRadius: 16
          }}>
                <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
                  <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase"
              }}>Monthly Payroll</span>
                  <div style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "#e0e7ff",
                color: "#4338ca",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                    <i className="fas fa-coins"></i>
                  </div>
                </div>
                <div style={{
              fontSize: 24,
              fontWeight: 800,
              color: "#1e1b4b",
              marginTop: 12,
              fontFamily: "'Poppins', sans-serif"
            }}>
                  ₹{totalPayroll.toLocaleString("en-IN")}
                </div>
                <div style={{
              fontSize: 11,
              color: "#64748b",
              marginTop: 4
            }}>For all active staff &amp; workers</div>
              </div>

              <div className="sal-card" style={{
            background: "#fff",
            padding: 20,
            borderRadius: 16
          }}>
                <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
                  <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase"
              }}>Disbursed</span>
                  <div style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "#d1fae5",
                color: "#059669",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                    <i className="fas fa-check"></i>
                  </div>
                </div>
                <div style={{
              fontSize: 24,
              fontWeight: 800,
              color: "#065f46",
              marginTop: 12,
              fontFamily: "'Poppins', sans-serif"
            }}>
                  ₹{totalPaid.toLocaleString("en-IN")}
                </div>
                <div style={{
              fontSize: 11,
              color: "#059669",
              marginTop: 4,
              fontWeight: 600
            }}>
                  {paidCount} of {employees.length} employees paid
                </div>
              </div>

              <div className="sal-card" style={{
            background: "#fff",
            padding: 20,
            borderRadius: 16
          }}>
                <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
                  <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase"
              }}>Pending Balance</span>
                  <div style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "#fee2e2",
                color: "#b91c1c",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                    <i className="fas fa-clock"></i>
                  </div>
                </div>
                <div style={{
              fontSize: 24,
              fontWeight: 800,
              color: "#991b1b",
              marginTop: 12,
              fontFamily: "'Poppins', sans-serif"
            }}>
                  ₹{remainingPay.toLocaleString("en-IN")}
                </div>
                <div style={{
              fontSize: 11,
              color: "#64748b",
              marginTop: 4
            }}>
                  {employees.length - paidCount} employees unpaid
                </div>
              </div>

              <div className="sal-card" style={{
            background: "#fff",
            padding: 20,
            borderRadius: 16
          }}>
                <div style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase"
            }}>Disbursement Status</div>
                <div style={{
              display: "flex",
              alignItems: "baseline",
              gap: 6,
              marginTop: 12
            }}>
                  <div style={{
                fontSize: 28,
                fontWeight: 800,
                color: "#1e293b",
                fontFamily: "'Poppins', sans-serif"
              }}>
                    {employees.length > 0 ? Math.round(paidCount / employees.length * 100) : 0}%
                  </div>
                  <span style={{
                fontSize: 11,
                color: "#64748b",
                fontWeight: 600
              }}>Completed</span>
                </div>
                <div style={{
              width: "100%",
              height: 8,
              background: "#e2e8f0",
              borderRadius: 4,
              marginTop: 12,
              overflow: "hidden"
            }}>
                  <div style={{
                width: `${employees.length > 0 ? paidCount / employees.length * 100 : 0}%`,
                height: "100%",
                background: "linear-gradient(90deg, #10b981, #34d399)",
                borderRadius: 4,
                transition: "width 0.4s ease"
              }}></div>
                </div>
              </div>
            </div>}

          {/* ===== TAB CONTENT ===== */}
          {loading ? <div style={{
          textAlign: "center",
          padding: "60px 0",
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #e2e8f0"
        }}>
              <div style={{
            fontSize: 32,
            color: "#6366f1",
            animation: "spin 1s linear infinite"
          }}>
                <i className="fas fa-circle-notch"></i>
              </div>
              <div style={{
            marginTop: 16,
            fontSize: 14,
            color: "#64748b",
            fontWeight: 500
          }}>Loading employee salary records...</div>
            </div> : error ? <div style={{
          background: "#fff",
          padding: "40px 24px",
          borderRadius: 16,
          border: "1px solid #fca5a5",
          textAlign: "center"
        }}>
              <div style={{
            fontSize: 40,
            color: "#ef4444",
            marginBottom: 12
          }}>
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div style={{
            fontSize: 15,
            fontWeight: 700,
            color: "#991b1b"
          }}>Error fetching salary details</div>
              <div style={{
            fontSize: 12,
            color: "#ef4444",
            marginTop: 4
          }}>{error}</div>
              <button onClick={fetchData} style={{
            marginTop: 16,
            padding: "8px 16px",
            background: "#ef4444",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer"
          }}>Retry</button>
            </div> : <>
              {/* ===== TAB 1: PAYMENTS ===== */}
              {activeTab === "payments" && <div style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            boxShadow: "0 2px 10px rgba(0,0,0,0.02)"
          }}>
                  
                  <div style={{
              padding: "16px 20px",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
              background: "#f8fafc"
            }}>
                    <div style={{
                position: "relative",
                minWidth: 260
              }}>
                      <i className="fas fa-search" style={{
                  position: "absolute",
                  left: 12,
                  top: 12,
                  color: "#94a3b8",
                  fontSize: 13
                }}></i>
                      <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search employee name or role..." style={{
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  padding: "8px 12px 8px 34px",
                  fontSize: 12,
                  width: "100%",
                  outline: "none",
                  color: "#1e293b"
                }} />
                    </div>
                    
                    <div style={{
                display: "flex",
                gap: 10
              }}>
                      <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#475569",
                  outline: "none",
                  cursor: "pointer"
                }}>
                        <option value="All">All Types</option>
                        <option value="Staff">Staff / Teachers</option>
                        <option value="Worker">Workers / Support</option>
                      </select>

                      <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#475569",
                  outline: "none",
                  cursor: "pointer"
                }}>
                        <option value="All">All Status</option>
                        <option value="Paid">Paid Only</option>
                        <option value="Unpaid">Unpaid Only</option>
                      </select>
                    </div>
                  </div>

                  {filteredEmployees.length === 0 ? <div style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#64748b"
            }}>
                      <i className="fas fa-users-slash" style={{
                fontSize: 32,
                marginBottom: 12,
                color: "#cbd5e1"
              }}></i>
                      <div style={{
                fontSize: 13,
                fontWeight: 600
              }}>No employees found matching the filters</div>
                    </div> : <div style={{
              overflowX: "auto"
            }}>
                      <table style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "left"
              }}>
                        <thead>
                          <tr style={{
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0"
                  }}>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Employee Details</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Category</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Base Salary</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Payment Status</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Paid Month</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredEmployees.map(emp => <tr key={emp._id} className="t-row" style={{
                    borderBottom: "1px solid #f1f5f9"
                  }}>
                              <td style={{
                      padding: "12px 20px"
                    }}>
                                <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12
                      }}>
                                  <div style={{
                          width: 38,
                          height: 38,
                          borderRadius: 10,
                          background: emp.type === "Staff" ? "linear-gradient(135deg, #6366f1, #4f46e5)" : "linear-gradient(135deg, #0d9488, #0f766e)",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 14
                        }}>
                                    {emp.name.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <div style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#1e293b"
                          }}>{emp.name}</div>
                                    <div style={{
                            fontSize: 11,
                            color: "#64748b",
                            marginTop: 2
                          }}>{emp.role}</div>
                                  </div>
                                </div>
                              </td>
                              <td style={{
                      padding: "12px 20px"
                    }}>
                                <span className={emp.type === "Staff" ? "badge-staff" : "badge-worker"} style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "3px 8px",
                        borderRadius: 20
                      }}>
                                  {emp.type}
                                </span>
                              </td>
                              <td style={{
                      padding: "12px 20px",
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#334155"
                    }}>
                                ₹{(emp.baseSalary || 0).toLocaleString("en-IN")}
                              </td>
                              <td style={{
                      padding: "12px 20px"
                    }}>
                                <span className={emp.status === "Paid" ? "badge-paid" : "badge-unpaid"} style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "3px 8px",
                        borderRadius: 20,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4
                      }}>
                                  <span style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: emp.status === "Paid" ? "#059669" : "#dc2626"
                        }} />
                                  {emp.status}
                                </span>
                              </td>
                              <td style={{
                      padding: "12px 20px",
                      fontSize: 12,
                      color: "#64748b"
                    }}>
                                {formatMonth(selectedMonth)}
                              </td>
                              <td style={{
                      padding: "12px 20px"
                    }}>
                                {emp.status === "Paid" ? <button onClick={() => setPayslipData(emp)} style={{
                        background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        padding: "6px 12px",
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        boxShadow: "0 2px 4px rgba(37,99,235,0.2)"
                      }}>
                                    <i className="fas fa-file-invoice-dollar"></i> View Slip
                                  </button> : <span style={{
                        fontSize: 11,
                        color: "#94a3b8",
                        fontWeight: 500,
                        fontStyle: "italic",
                        background: "#f1f5f9",
                        padding: "6px 12px",
                        borderRadius: 8,
                        display: "inline-block"
                      }}>
                                    <i className="fas fa-lock" style={{
                          marginRight: 4
                        }}></i> Not Paid
                                  </span>}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>}
                </div>}

              {/* ===== TAB 2: HISTORY ===== */}
              {activeTab === "history" && <div style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            boxShadow: "0 2px 10px rgba(0,0,0,0.02)"
          }}>
                  <div style={{
              padding: "16px 20px",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
              background: "#f8fafc"
            }}>
                    <div style={{
                display: "flex",
                alignItems: "center",
                gap: 8
              }}>
                      <i className="fas fa-receipt" style={{
                  color: "#6366f1"
                }}></i>
                      <div style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1e293b"
                }}>Disbursement Log</div>
                      <span style={{
                  fontSize: 10,
                  color: "#94a3b8",
                  fontWeight: 600,
                  marginLeft: 6
                }}>
                        ({transactions.length} records)
                      </span>
                    </div>
                    <div style={{
                display: "flex",
                gap: 10
              }}>
                      <button onClick={fetchAllTransactions} style={{
                  background: "#fff",
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#475569",
                  cursor: "pointer"
                }}>
                        <i className="fas fa-sync-alt" style={{
                    marginRight: 4
                  }}></i> Refresh
                      </button>
                    </div>
                  </div>

                  {transactions.length === 0 ? <div style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#64748b"
            }}>
                      <i className="fas fa-history" style={{
                fontSize: 32,
                marginBottom: 12,
                color: "#cbd5e1"
              }}></i>
                      <div style={{
                fontSize: 13,
                fontWeight: 600
              }}>No transaction records found</div>
                    </div> : <div style={{
              overflowX: "auto"
            }}>
                      <table style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "left"
              }}>
                        <thead>
                          <tr style={{
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0"
                  }}>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Tx Date</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Employee</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Role / Type</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Paid Month</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Amount Paid</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Method</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Tx Ref ID</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Slip</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map(tx => <tr key={tx._id} className="t-row" style={{
                    borderBottom: "1px solid #f1f5f9"
                  }}>
                              <td style={{
                      padding: "12px 20px",
                      fontSize: 11,
                      color: "#64748b"
                    }}>
                                {new Date(tx.paymentDate).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                              </td>
                              <td style={{
                      padding: "12px 20px"
                    }}>
                                <div style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#1e293b"
                      }}>{tx.employeeName}</div>
                              </td>
                              <td style={{
                      padding: "12px 20px"
                    }}>
                                <div style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#475569"
                      }}>{tx.employeeRole}</div>
                                <span className={tx.employeeType === "Staff" ? "badge-staff" : "badge-worker"} style={{
                        fontSize: 9,
                        fontWeight: 700,
                        padding: "1px 6px",
                        borderRadius: 10,
                        marginTop: 4,
                        display: "inline-block"
                      }}>
                                  {tx.employeeType}
                                </span>
                              </td>
                              <td style={{
                      padding: "12px 20px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#334155"
                    }}>
                                {formatMonth(tx.month)}
                              </td>
                              <td style={{
                      padding: "12px 20px",
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#059669"
                    }}>
                                ₹{tx.paidAmount.toLocaleString("en-IN")}
                              </td>
                              <td style={{
                      padding: "12px 20px",
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#475569"
                    }}>
                                <span style={{
                        padding: "2px 6px",
                        background: "#f1f5f9",
                        border: "1px solid #e2e8f0",
                        borderRadius: 6
                      }}>
                                  {tx.paymentMethod}
                                </span>
                              </td>
                              <td style={{
                      padding: "12px 20px",
                      fontSize: 11,
                      fontFamily: "monospace",
                      color: "#64748b"
                    }}>
                                {tx.transactionId}
                              </td>
                              <td style={{
                      padding: "12px 20px"
                    }}>
                                <button onClick={() => setPayslipData({
                        _id: tx.employeeId,
                        name: tx.employeeName,
                        role: tx.employeeRole,
                        type: tx.employeeType,
                        phone: tx.employeePhone || '',
                        baseSalary: tx.baseSalary,
                        paymentDetails: {
                          transactionId: tx.transactionId,
                          paidAmount: tx.paidAmount,
                          paymentDate: tx.paymentDate,
                          paymentMethod: tx.paymentMethod,
                          remarks: tx.remarks
                        }
                      })} style={{
                        background: "#f1f5f9",
                        border: "1px solid #cbd5e1",
                        borderRadius: 8,
                        padding: "5px 10px",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#1e293b",
                        cursor: "pointer"
                      }} title="View Payslip">
                                  <i className="fas fa-file-invoice-dollar" style={{
                          color: "#2563eb"
                        }}></i> Slip
                                </button>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>}
                </div>}
            </>}
        </div>
      </div>

      {/* ===== PAYSLIP MODAL WITH WHATSAPP SHARE BUTTON ===== */}
      {payslipData && <div style={{
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(0,0,0,0.65)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(6px)"
    }}>
          <div style={{
        background: "#fff",
        borderRadius: 20,
        width: "min(600px, 95%)",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "0 25px 70px rgba(0,0,0,0.25)"
      }}>
            
            {/* Modal Header with WhatsApp Share Button */}
            <div style={{
          background: "#1e293b",
          padding: "12px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
          color: "#fff"
        }}>
              <span style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.5px"
          }}>
                <i className="fas fa-file-invoice-dollar" style={{
              marginRight: 6
            }}></i> PAYSLIP RECEIPT
              </span>
              <div style={{
            display: "flex",
            gap: 8,
            alignItems: "center"
          }}>
                <button onClick={() => {
              const empData = payslipData;
              setPayslipData(null);
              setTimeout(() => {
                handleWhatsAppShare(empData);
              }, 300);
            }} className="whatsapp-share-btn" style={{
              background: "#25D366",
              color: "white",
              border: "none",
              padding: "6px 14px",
              borderRadius: "30px",
              fontSize: "11px",
              fontWeight: "600",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              boxShadow: "0 2px 8px rgba(37, 211, 102, 0.3)"
            }} title="Share via WhatsApp">
                  <i className="fab fa-whatsapp" style={{
                fontSize: "14px"
              }}></i> Share
                </button>
                <button onClick={() => window.print()} style={{
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "5px 12px",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 4
            }}>
                  <i className="fas fa-print"></i> Print
                </button>
                <button onClick={() => setPayslipData(null)} style={{
              background: "rgba(255,255,255,0.15)",
              border: "none",
              color: "#fff",
              width: 26,
              height: 26,
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
                  <span style={{
                fontSize: 14,
                fontWeight: 700
              }}>&times;</span>
                </button>
              </div>
            </div>

            {/* Payslip Document Area */}
            <div style={{
          flex: 1,
          overflowY: "auto",
          padding: 24
        }}>
              <div id="payslip-print-area" style={{
            border: "1px solid #cbd5e1",
            padding: 32,
            borderRadius: 12,
            backgroundColor: "#fff"
          }}>
                
                {/* Header */}
                <div style={{
              textAlign: "center",
              borderBottom: "2px double #94a3b8",
              paddingBottom: 16
            }}>
                  <div style={{
                fontSize: 20,
                fontWeight: 800,
                color: "#1e1b4b",
                fontFamily: "'Poppins', sans-serif",
                letterSpacing: "1px"
              }}>{schoolName.toUpperCase()} SMART CAMPUS</div>
                  <div style={{
                fontSize: 10,
                color: "#475569",
                marginTop: 4
              }}>Affiliated Secondary &amp; Higher Secondary School</div>
                  <div style={{
                fontSize: 9,
                color: "#64748b",
                marginTop: 2
              }}>Salem, Tamil Nadu, India | Contact: support@{getSchoolEmailDomain(schoolName)}</div>
                  <div style={{
                background: "#f1f5f9",
                padding: "6px 12px",
                borderRadius: 6,
                display: "inline-block",
                marginTop: 12,
                fontSize: 11,
                fontWeight: 700,
                color: "#312e81",
                letterSpacing: "0.5px"
              }}>
                    SALARY ADVICE SLIP &middot; {formatMonth(selectedMonth).toUpperCase()}
                  </div>
                </div>

                {/* Employee Details Table */}
                <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginTop: 20,
              fontSize: 11,
              color: "#334155"
            }}>
                  <div>
                    <div style={{
                  display: "flex",
                  padding: "4px 0"
                }}>
                      <span style={{
                    width: 130,
                    fontWeight: 600,
                    color: "#64748b"
                  }}>Employee Name:</span>
                      <span style={{
                    fontWeight: 700,
                    color: "#1e293b"
                  }}>{payslipData.name}</span>
                    </div>
                    <div style={{
                  display: "flex",
                  padding: "4px 0"
                }}>
                      <span style={{
                    width: 130,
                    fontWeight: 600,
                    color: "#64748b"
                  }}>Designation/Role:</span>
                      <span>{payslipData.role}</span>
                    </div>
                    <div style={{
                  display: "flex",
                  padding: "4px 0"
                }}>
                      <span style={{
                    width: 130,
                    fontWeight: 600,
                    color: "#64748b"
                  }}>Employee Category:</span>
                      <span>{payslipData.type}</span>
                    </div>
                  </div>
                  <div>
                    <div style={{
                  display: "flex",
                  padding: "4px 0"
                }}>
                      <span style={{
                    width: 130,
                    fontWeight: 600,
                    color: "#64748b"
                  }}>Salary Month:</span>
                      <span style={{
                    fontWeight: 600
                  }}>{formatMonth(selectedMonth)}</span>
                    </div>
                    <div style={{
                  display: "flex",
                  padding: "4px 0"
                }}>
                      <span style={{
                    width: 130,
                    fontWeight: 600,
                    color: "#64748b"
                  }}>Receipt Date:</span>
                      <span>{payslipData.paymentDetails?.paymentDate ? new Date(payslipData.paymentDetails.paymentDate).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "long",
                      year: "numeric"
                    }) : new Date().toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "long",
                      year: "numeric"
                    })}</span>
                    </div>
                    <div style={{
                  display: "flex",
                  padding: "4px 0"
                }}>
                      <span style={{
                    width: 130,
                    fontWeight: 600,
                    color: "#64748b"
                  }}>Payment Mode:</span>
                      <span style={{
                    fontWeight: 600
                  }}>{payslipData.paymentDetails?.paymentMethod || "Bank Transfer"}</span>
                    </div>
                  </div>
                </div>

                {/* Pay Details Table */}
                <table style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: 24,
              fontSize: 11
            }}>
                  <thead>
                    <tr style={{
                  background: "#f8fafc",
                  borderBottom: "1px solid #cbd5e1",
                  borderTop: "1px solid #cbd5e1"
                }}>
                      <th style={{
                    padding: "8px 12px",
                    textAlign: "left",
                    color: "#475569"
                  }}>PAY DETAILS</th>
                      <th style={{
                    padding: "8px 12px",
                    textAlign: "right",
                    color: "#475569"
                  }}>AMOUNT (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{
                  borderBottom: "1px solid #f1f5f9"
                }}>
                      <td style={{
                    padding: "10px 12px",
                    color: "#334155"
                  }}>
                        <strong>Basic monthly base salary rate</strong>
                        <div style={{
                      fontSize: 9,
                      color: "#94a3b8",
                      marginTop: 2
                    }}>Contractual basic wages structure</div>
                      </td>
                      <td style={{
                    padding: "10px 12px",
                    textAlign: "right",
                    fontWeight: 600
                  }}>
                        ₹{Number(payslipData.baseSalary || 0).toLocaleString("en-IN")}
                      </td>
                    </tr>
                    <tr style={{
                  borderBottom: "1px solid #cbd5e1"
                }}>
                      <td style={{
                    padding: "10px 12px",
                    color: "#334155"
                  }}>
                        <strong>Disbursed Salary Amount</strong>
                        <div style={{
                      fontSize: 9,
                      color: "#94a3b8",
                      marginTop: 2
                    }}>Actual net pay distributed</div>
                      </td>
                      <td style={{
                    padding: "10px 12px",
                    textAlign: "right",
                    fontWeight: 600
                  }}>
                        ₹{Number(payslipData.paymentDetails?.paidAmount || payslipData.baseSalary || 0).toLocaleString("en-IN")}
                      </td>
                    </tr>
                    <tr style={{
                  background: "#f8fafc",
                  fontWeight: 700,
                  fontSize: 12,
                  borderBottom: "2px solid #1e293b"
                }}>
                      <td style={{
                    padding: "12px 12px",
                    color: "#1e1b4b"
                  }}>NET DISBURSED AMOUNT</td>
                      <td style={{
                    padding: "12px 12px",
                    textAlign: "right",
                    color: "#1e1b4b",
                    fontSize: 13
                  }}>
                        ₹{Number(payslipData.paymentDetails?.paidAmount || payslipData.baseSalary || 0).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Transaction Details */}
                <div style={{
              marginTop: 20,
              background: "#f8fafc",
              borderRadius: 8,
              padding: "12px 16px",
              border: "1px solid #e2e8f0",
              fontSize: 10,
              color: "#64748b",
              fontFamily: "monospace"
            }}>
                  <div>TRANSACTION REFERENCE ID: {payslipData.paymentDetails?.transactionId || "N/A"}</div>
                  <div style={{
                marginTop: 4
              }}>STATUS: PAID &amp; SETTLED</div>
                  {payslipData.paymentDetails?.remarks && <div style={{
                marginTop: 6,
                fontStyle: "italic",
                fontFamily: "'Inter', sans-serif"
              }}>
                      <strong>Remarks:</strong> {payslipData.paymentDetails.remarks}
                    </div>}
                </div>

                {/* Signatures */}
                <div style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 60,
              fontSize: 11,
              color: "#475569"
            }}>
                  <div style={{
                textAlign: "center",
                width: 150
              }}>
                    <div style={{
                  borderBottom: "1px solid #94a3b8",
                  height: 30
                }}></div>
                    <div style={{
                  marginTop: 6,
                  fontWeight: 600
                }}>Signature of Employee</div>
                  </div>
                  <div style={{
                textAlign: "center",
                width: 150
              }}>
                    <div style={{
                  borderBottom: "1px solid #94a3b8",
                  height: 30,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center"
                }}>
                      <span style={{
                    fontSize: 9,
                    fontStyle: "italic",
                    color: "#94a3b8",
                    marginBottom: 2
                  }}>{schoolName} Management</span>
                    </div>
                    <div style={{
                  marginTop: 6,
                  fontWeight: 600
                }}>Principal / HM Authorised</div>
                  </div>
                </div>

                {/* Close Receipt Text */}
                <div style={{
              textAlign: "center",
              marginTop: 20,
              fontSize: 10,
              color: "#94a3b8",
              borderTop: "1px solid #e2e8f0",
              paddingTop: 12
            }}>
                  CLOSED RECEIPT
                </div>

              </div>
            </div>

            {/* Bottom Close Panel */}
            <div style={{
          background: "#f8fafc",
          padding: "12px 20px",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0
        }}>
              <button onClick={() => {
            const empData = payslipData;
            setPayslipData(null);
            setTimeout(() => handleWhatsAppShare(empData), 300);
          }} className="whatsapp-share-btn" style={{
            background: "#25D366",
            color: "white",
            border: "none",
            padding: "8px 18px",
            borderRadius: "30px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            boxShadow: "0 2px 8px rgba(37, 211, 102, 0.3)"
          }}>
                <i className="fab fa-whatsapp" style={{
              fontSize: "16px"
            }}></i> Share on WhatsApp
              </button>
              <button onClick={() => setPayslipData(null)} style={{
            padding: "8px 16px",
            background: "#f1f5f9",
            border: "1px solid #cbd5e1",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            color: "#475569",
            cursor: "pointer"
          }}>
                Close Receipt
              </button>
            </div>

          </div>
        </div>}
    </>;
}